import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import { getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from "../../utils/dateUtils";
import { mergeFormDataWithStatusExtras } from "../../utils/applicationFormPayload";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import ApplicationFormFooter from './ApplicationFormFooter';
import { chhaPostTestSchema } from "./chhaPostTest.validation";
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";
import PropTypes from 'prop-types';
import { HHA_EXAM_ITEMS } from "./chhaPostTestConstants";
import { getStandardizedSignatures } from '../../utils/formHelpers';


const ChhaPostTest = ({ positionName = "HHA", onClose }) => {
    const [page, setPage] = useState(1);
    const [groupId, setGroupId] = useState(null);
    const [currentStatus, setCurrentStatus] = useState("");

    const methods = useForm({
        resolver: zodResolver(chhaPostTestSchema),
        defaultValues: {
            tenantId: undefined,
            userId: undefined,
            applicantName: "",
            testDate: parseIsoDateToMDYUS(new Date().toISOString()),
            applicantSignature: "",
            applicantSignedAt: parseIsoDateToMDYUS(new Date().toISOString()),
            reviewedBySignature: "",
            reviewedBySignedAt: "",
            reviewedBySignedBy: "",
            tests: [{
                testType: "chha_post_test",
                testTitle: "CHHA Post Test",
                answers: {}
            }]
        },
        mode: "onChange"
    });

    const { handleSubmit, watch, reset, getValues, formState: { errors } } = methods;

    const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
        const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
        try {
            // Flatten answers for the backend: { "1": "A", "2": "C", ... }
            const flatAnswers = {};
            if (data.tests?.[0]?.answers) {
                Object.keys(data.tests[0].answers).forEach(qId => {
                    flatAnswers[qId] = data.tests[0].answers[qId].answer;
                });
            }

            const merged = mergeFormDataWithStatusExtras(data, additionalData);
            const payload = {
                ...merged,
                tenantId: merged.tenantId,
                userId: merged.userId,
                applicantName: merged.applicantName,
                testDate: merged.testDate ? toIsoDate(merged.testDate) : "",
                applicantSignature: merged.applicantSignature,
                applicantSignedAt: toIsoDate(merged.applicantSignedAt) || new Date().toISOString(),
                reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt) || (merged.reviewedBySignature ? new Date().toISOString() : ""),
                answers: flatAnswers,
                flag: actualFlag,
            };

            // Handle To Be Corrected comments
            const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
            if (finalComments) {
                payload.comments = finalComments;
            }

            let response;
            if (groupId) {
                response = await request(`/chha-competency-tests/${groupId}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
            } else {
                response = await request('/chha-competency-tests', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            }

            if (response.status === 1) {
                toast.success(response.message || "Form processed successfully!");
                closeForm();
            } else {
                toast.error(response.message || "Failed to process form!");
            }
        } catch (error) {
            console.error("Error processing form:", error);
            toast.error(`Error: ${error.message}`);
        }
    };

    const {
        targetUserId,
        isSignatureModalOpen,
        openSignatureModal,
        closeSignatureModal,
        signerName,
        handleSignatureApply,
        user: currentUserData,
        isAdmin,
        isReadOnly,
        handleStatusAction,
        handleSave,
        closeForm,
        actualFromId
    } = useApplicationFormLogic({
        methods,
        applicantNameField: "applicantName",
        applicantSignatureField: "applicantSignature",
        applicantSignatureDateField: "applicantSignedAt",
        positionName,
        onClose,
        onSubmit,
        currentStatus
    });

    const isHeaderDisabled = isReadOnly || (!isAdmin && currentStatus === 'send_to_office');

    useEffect(() => {
        const fetchPostTest = async () => {
            if (!currentUserData) return;
            if (isAdmin && !targetUserId && !groupId) return;

            try {
                if (!actualFromId && !groupId) {
                    const ownerId = targetUserId || currentUserData.id;
                    reset({
                        tenantId: resolveFormTenantId(currentUserData?.tenantId),
                        userId: ownerId,
                        applicantName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
                        testDate: parseIsoDateToMDYUS(new Date().toISOString()),
                        applicantSignedAt: parseIsoDateToMDYUS(new Date().toISOString()),
                        tests: getValues().tests
                    });
                    setCurrentStatus("");
                    return;
                }

                let response;
                if (isAdmin) {
                    if (actualFromId) {
                        response = await request(`/chha-competency-tests/${actualFromId}?userId=${targetUserId}`);
                    } else {
                        response = await request(`/chha-competency-tests?userId=${targetUserId}&limit=1`);
                    }
                } else {
                    response = await request(`/chha-competency-tests/${actualFromId || targetUserId}`);
                    if (!response || !response.result) {
                        response = await request(`/chha-competency-tests?userId=${targetUserId}&limit=1`);
                    }
                }

                const resultData = response.result || response;
                const testData = resultData.test || (Array.isArray(resultData.items) ? resultData.items[0] : (resultData.id ? resultData : null));

                if (response.status === 1 && testData) {
                    setGroupId(testData.id);
                    setCurrentStatus(testData.status || "");

                    // Map flat answers back to nested structure
                    const nestedAnswers = {};
                    if (testData.answers) {
                        Object.keys(testData.answers).forEach(qId => {
                            const question = HHA_EXAM_ITEMS.find(q => q.id === qId);
                            nestedAnswers[qId] = {
                                answer: testData.answers[qId] === null ? "" : testData.answers[qId],
                                type: question?.type || "multiple_choice"
                            };
                        });
                    }

                    reset({
                        tenantId: testData.tenantId,
                        userId: testData.userId,
                        applicantName: testData.applicantName || "",
                        testDate: parseIsoDateToMDYUS(testData.testDate) || "",
                        ...getStandardizedSignatures(testData, isAdmin),
                        reviewedBySignature: testData.reviewedBySignature || testData.adminSignature || "",
                        reviewedBySignedAt: parseIsoDateToMDYUS(testData.reviewedBySignedAt || testData.adminSignatureDate || testData.reviewedBySignatureDate),
                        reviewedBySignedBy: testData.reviewedBySignedBy || "",
                        applicantSignature: testData.applicantSignature || "",
                        applicantSignedAt: parseIsoDateToMDYUS(testData.applicantSignedAt || testData.applicantSignatureDate || testData.reviewedBySignatureDate),
                        tests: [{
                            testType: "chha_post_test",
                            testTitle: "CHHA Post Test",
                            answers: nestedAnswers,
                            scoringResult: testData.scoringResult || null
                        }],
                        comments: testData.comments || ""
                    });
                }
            } catch (error) {
                console.error("Error fetching post test:", error);
            }
        };

        fetchPostTest();
    }, [targetUserId, reset, getValues, currentUserData, isAdmin, groupId, actualFromId]);

    const handleOnNext = async () => {
        if (isAdmin || actualFromId || groupId) {
            setPage(p => Math.min(5, p + 1));
            return;
        }

        const questions = getQuestionsForPage(page);
        const fieldsToValidate = questions.map(q => `tests.0.answers.${q.id}.answer`);

        const result = await methods.trigger(["applicantName", "testDate", ...fieldsToValidate]);
        if (result) {
            setPage(p => Math.min(5, p + 1));
        } else {
            toast.error("Please answer all questions on this page.");
        }
    };

    const renderQuestion = (q, testIndex) => {
        const fieldName = `tests.${testIndex}.answers.${q.id}.answer`;
        const fieldError = errors?.tests?.[testIndex]?.answers?.[q.id]?.answer;

        return (
            <div key={q.id} className="mb-6 p-4 bg-white rounded-lg border border-gray-100 shadow-sm transition-all hover:shadow-md">
                <input
                    type="hidden"
                    {...methods.register(`tests.${testIndex}.answers.${q.id}.type`)}
                    value={q.type}
                />

                <p className="mb-4 font-semibold text-gray-800 flex gap-2">
                    <span className="text-blue-600 font-bold">{q.id}.</span>
                    {q.text}
                </p>

                {q.image && (
                    <div className="mb-4 ml-6">
                        <img
                            src={q.image}
                            alt={`Question ${q.id}`}
                            className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm"
                        />
                    </div>
                )}

                <div className="ml-6 space-y-3">
                    {q.type === "true_false" && (
                        <div className="flex flex-wrap gap-8">
                            <label className="flex items-center cursor-pointer group">
                                <input
                                    type="radio"
                                    value="T"
                                    {...methods.register(fieldName)}
                                    disabled={isReadOnly}
                                    className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 transition-colors cursor-pointer"
                                />
                                <span className="ml-2 text-gray-700 font-medium group-hover:text-blue-600 transition-colors text-lg">True</span>
                            </label>
                            <label className="flex items-center cursor-pointer group">
                                <input
                                    type="radio"
                                    value="F"
                                    {...methods.register(fieldName)}
                                    disabled={isReadOnly}
                                    className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 transition-colors cursor-pointer"
                                />
                                <span className="ml-2 text-gray-700 font-medium group-hover:text-blue-600 transition-colors text-lg">False</span>
                            </label>
                        </div>
                    )}

                    {q.type === "multiple_choice" && (
                        <div className="grid grid-cols-1 gap-3">
                            {q.options?.map((opt) => (
                                <label
                                    key={opt.label}
                                    className="flex items-start cursor-pointer group p-2 hover:bg-gray-50 rounded-md transition-colors"
                                >
                                    <input
                                        type="radio"
                                        value={opt.label}
                                        {...methods.register(fieldName)}
                                        disabled={isReadOnly}
                                        className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <div className="ml-3">
                                        <span className="font-bold text-blue-600 mr-1">{opt.label}.</span>
                                        <span className="text-gray-700 group-hover:text-gray-900">{opt.text}</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
                {fieldError && <p className="text-red-500 text-sm mt-2 ml-6 animate-pulse">{fieldError.message || "Required"}</p>}
            </div>
        );
    };

    const getQuestionsForPage = (pageNum) => {
        const start = (pageNum - 1) * 10;
        return HHA_EXAM_ITEMS.slice(start, start + 10);
    };

    const getPageTitle = () => {
        return `CHHA Post Test (Page ${page}/5)`;
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
            <CloseButton onClose={closeForm} />

            <div className="max-w-5xl mx-auto bg-white shadow-xl overflow-hidden">
                <EvaluationFormHeader
                    title={getPageTitle()}
                    subtitle="Certified Home Health Aide Competency Test"
                />

                <FormProvider {...methods}>
                    <div className="mb-0 pb-6 border-b border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center p-4">
                            <div>
                                <EvaluationInput
                                    name="applicantName"
                                    label="Clinician / Orientee Name:"
                                    placeholder="Name"
                                    disabled={isHeaderDisabled}
                                />
                            </div>
                            <div>
                                <label htmlFor="testDate" className="block text-sm font-medium text-gray-700 mb-1">
                                    Date
                                </label>
                                <InputDatePicker
                                    formatSeparator="/"
                                    name="testDate"
                                    id="testDate"
                                    max={getLocalSystemDate()}
                                    disabled={isHeaderDisabled}
                                />
                            </div>
                        </div>
                    </div>

                    <form
                        className="space-y-6 p-8 min-h-[600px]"
                        onSubmit={handleSubmit((d) => onSubmit(d, 'send_to_office'))}>
                        <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />

                        <fieldset disabled={isReadOnly} className="space-y-8 mt-6">
                            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-8">
                                <p className="text-blue-800 font-semibold italic">Please select the best answer for the following questions to demonstrate your competency as a Certified Home Health Aide.</p>
                            </div>

                            <div className="space-y-6">
                                {getQuestionsForPage(page).map(q => renderQuestion(q, 0))}
                            </div>

                            {page === 5 && isAdmin && (
                                <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                                    <h3 className="text-xl font-bold mb-4 text-blue-800">Post Test Scoring Results</h3>
                                    <div className="grid grid-cols-1 gap-4">
                                        {watch("tests")?.map((test) => (
                                            <div key={test.testType} className="p-4 bg-white rounded shadow-sm border border-gray-100">
                                                <h4 className="font-semibold text-gray-800 mb-2 border-b pb-1">{test.testTitle || "CHHA Post Test"}</h4>
                                                {test.scoringResult ? (
                                                    <div className="text-sm space-y-2">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Score:</span>
                                                            <span className="font-medium">{test.scoringResult.score} / {test.scoringResult.totalQuestions}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Correct:</span>
                                                            <span className="text-green-600 font-medium">{test.scoringResult.correctAnswers}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Incorrect:</span>
                                                            <span className="text-red-500 font-medium">{test.scoringResult.incorrectAnswers}</span>
                                                        </div>
                                                        <div className="mt-2 pt-2 border-t flex justify-between items-center">
                                                            <span className="font-bold text-gray-700">Percentage:</span>
                                                            <span className={`text-lg font-bold ${test.scoringResult.percentage >= 80 ? 'text-green-600' : 'text-orange-500'}`}>
                                                                {test.scoringResult.percentage}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 italic text-center py-2">No score available</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </fieldset>

                        <ApplicationFormFooter
                            page={page}
                            setPage={setPage}
                            totalPages={5}
                            methods={methods}
                            onSave={handleSave}
                            onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                            onStatusUpdate={handleStatusAction}
                            onNext={handleOnNext}
                            isSignatureModalOpen={isSignatureModalOpen}
                            openSignatureModal={openSignatureModal}
                            closeSignatureModal={closeSignatureModal}
                            handleSignatureApply={handleSignatureApply}
                            signerName={signerName}
                            showCertification={false}
                            requireSignatures={true}
                            showSignatureSections={page === 5}
                            applicantName={watch("applicantName") || ""}
                            applicantSignature={watch("applicantSignature") || ""}
                            applicantSignedAt={watch("applicantSignedAt") || ""}
                            reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                            reviewedBySignature={watch("reviewedBySignature") || ""}
                            reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                            submitLabel="Submit All"
                            isReadOnly={isReadOnly}
                            onClose={closeForm}
                            currentStatus={currentStatus}
                            skipValidation={isAdmin || actualFromId || groupId}
                        />
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

ChhaPostTest.propTypes = {
    positionName: PropTypes.string,
    onClose: PropTypes.func
};

export default ChhaPostTest;
